import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { contactDetailPath } from "../routes/paths";
import { formatError } from "../lib/errors";
import type { DetailRouteContext } from "../hooks/useDetailRoute";

interface ContactRow {
  id: string;
  full_name: string | null;
  primary_email: string | null;
}

async function fetchByDeal(dealId: string): Promise<ContactRow[]> {
  const { data: deal, error: dealErr } = await supabase
    .schema("manna")
    .from("deals")
    .select("primary_person_id")
    .eq("id", dealId)
    .is("deleted_at", null)
    .maybeSingle();
  if (dealErr) throw dealErr;
  if (!deal?.primary_person_id) return [];
  const { data, error } = await supabase
    .schema("platform")
    .from("persons")
    .select("id, full_name, primary_email")
    .eq("id", deal.primary_person_id)
    .is("deleted_at", null)
    .limit(1);
  if (error) throw error;
  return (data ?? []) as ContactRow[];
}

async function fetchByOrg(orgId: string): Promise<ContactRow[]> {
  const { data: deals, error: dealErr } = await supabase
    .schema("manna")
    .from("deals")
    .select("primary_person_id")
    .eq("customer_organization_id", orgId)
    .is("deleted_at", null)
    .not("primary_person_id", "is", null);
  if (dealErr) throw dealErr;
  const ids = Array.from(
    new Set((deals ?? []).map((d) => d.primary_person_id).filter((id): id is string => !!id)),
  ).slice(0, 25);
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .schema("platform")
    .from("persons")
    .select("id, full_name, primary_email")
    .in("id", ids)
    .is("deleted_at", null)
    .order("full_name");
  if (error) throw error;
  return (data ?? []) as ContactRow[];
}

async function fetchByPartner(partnerId: string): Promise<ContactRow[]> {
  const { data, error } = await supabase
    .schema("manna")
    .from("persons_view")
    .select("id, full_name, primary_email")
    .eq("partner_organization_id", partnerId)
    .order("full_name")
    .limit(25);
  if (error) throw error;
  return (data ?? []) as ContactRow[];
}

export function RelatedContactsList({ ctx }: { ctx: DetailRouteContext }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["related_contacts", ctx.kind, ctx.id],
    enabled: ctx.kind !== "contact",
    queryFn: async () => {
      if (ctx.kind === "deal") return fetchByDeal(ctx.id);
      if (ctx.kind === "organization") return fetchByOrg(ctx.id);
      if (ctx.kind === "partner") return fetchByPartner(ctx.id);
      return [];
    },
  });

  if (ctx.kind === "contact") {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 1 }}>
        Related contacts not tracked for this entity.
      </Typography>
    );
  }
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (isError) return <Alert severity="error">{formatError(error)}</Alert>;
  if (!data || data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 1 }}>
        No related contacts.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      {data.map((c) => (
        <Box
          key={c.id}
          component={RouterLink}
          to={contactDetailPath(c.id)}
          sx={{
            textDecoration: "none",
            color: "inherit",
            py: 0.5,
            "&:hover .contact-name": { textDecoration: "underline" },
          }}
        >
          <Typography variant="body2" noWrap className="contact-name">
            {c.full_name || "(unnamed)"}
          </Typography>
          {c.primary_email && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
              {c.primary_email}
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
}
